/**
 * RELOAD ON RELEASE: a tab whose bundle is no longer the deployed one reloads itself.
 *
 * Why it must (#982): a release can change what a stored config MEANS, not only how it is
 * drawn. The signed-amount release relabels the amount columns and the server's v4 step
 * migrates old views at read time; a tab still running the previous bundle saves the views
 * it loaded earlier with the Reporting amount visible, stamped v4, and the server can never
 * migrate them again. `lazyWithRetry` only recovers a chunk that 404s, which a tab that has
 * already loaded every chunk it needs never hits.
 *
 * How it knows, without a dependency or a build step: the CRA build writes
 * `asset-manifest.json` beside `index.html`, naming the entry bundle by its content hash
 * (`files['main.js']`). The document loaded its own entry as a `<script>`, so a manifest
 * naming a script this document did not load means a newer build is deployed. In
 * `craco start` the entry is the unhashed `bundle.js` on both sides, so nothing ever differs.
 *
 * When: every few minutes, and whenever the tab comes back into view or back online — a tab
 * left open across a deploy is the case this exists for. Never in a loop: the build a reload
 * was made for is remembered for the session, so a document still stale after it (an
 * `index.html` held by a cache) stays put rather than reloading forever.
 */

/** The build a reload was made for — `sessionStorage`, so it outlives the reload only. */
export const BUILD_RELOAD_KEY = 'aivo-build-reload';

/** How often a tab asks, in ms. */
export const BUILD_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** The entry bundle a manifest names, as a path (`/static/js/main.1a2b.js`), or `null`. */
export const deployedEntryOf = (manifest: unknown): string | null => {
  if (typeof manifest !== 'object' || manifest === null) return null;
  const files = (manifest as { files?: unknown }).files;
  if (typeof files !== 'object' || files === null) return null;
  const entry = (files as Record<string, unknown>)['main.js'];
  return typeof entry === 'string' && entry.length > 0 ? entry : null;
};

const pathOf = (src: string, base: string): string | null => {
  try {
    return new URL(src, base).pathname;
  } catch {
    return null;
  }
};

/**
 * Is the deployed entry one of the scripts this document loaded? Compared by PATH, so a
 * bundle served from another host (a `PUBLIC_URL` CDN) still matches its manifest entry.
 */
export const isLoadedEntry = (entry: string, scriptSrcs: readonly string[], base: string) => {
  const target = pathOf(entry, base);
  return target !== null && scriptSrcs.some((src) => pathOf(src, base) === target);
};

export interface BuildCheckDeps {
  /** The deployed manifest, parsed; rejects or resolves `null` when it cannot be read. */
  fetchManifest: () => Promise<unknown>;
  /** The `src` of every script the document loaded. */
  scriptSrcs: () => readonly string[];
  /** The document's URL, to resolve relative paths against. */
  base: string;
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  reload: () => void;
}

/** What one check found: the tab runs the deployed build, reloads for a newer one, or cannot tell. */
export type BuildCheckOutcome = 'current' | 'reloading' | 'held' | 'unknown';

/**
 * One check. An unreadable manifest is `unknown` and changes nothing: an offline tab or a
 * deploy in progress is no reason to reload. A stale document reloads once per deployed
 * build (`held` after that), and a current one forgets the build it reloaded for.
 */
export async function checkDeployedBuild(deps: BuildCheckDeps): Promise<BuildCheckOutcome> {
  let manifest: unknown;
  try {
    manifest = await deps.fetchManifest();
  } catch {
    return 'unknown';
  }
  const entry = deployedEntryOf(manifest);
  if (entry === null) return 'unknown';
  if (isLoadedEntry(entry, deps.scriptSrcs(), deps.base)) {
    deps.storage.removeItem(BUILD_RELOAD_KEY);
    return 'current';
  }
  if (deps.storage.getItem(BUILD_RELOAD_KEY) === entry) return 'held';
  deps.storage.setItem(BUILD_RELOAD_KEY, entry);
  deps.reload();
  return 'reloading';
}

const browserDeps = (): BuildCheckDeps => ({
  fetchManifest: async () => {
    const response = await fetch(`${process.env.PUBLIC_URL ?? ''}/asset-manifest.json`, {
      cache: 'no-store',
    });
    return response.ok ? ((await response.json()) as unknown) : null;
  },
  scriptSrcs: () => Array.from(document.scripts, (script) => script.src).filter(Boolean),
  base: window.location.href,
  storage: window.sessionStorage,
  reload: () => window.location.reload(),
});

/**
 * Start checking; returns the function that stops. One check at a time: a slow manifest
 * read never stacks a second one behind it.
 */
export function watchDeployedBuild(
  deps: BuildCheckDeps = browserDeps(),
  intervalMs: number = BUILD_CHECK_INTERVAL_MS,
): () => void {
  let inFlight = false;
  const check = () => {
    if (inFlight) return;
    inFlight = true;
    void checkDeployedBuild(deps).finally(() => {
      inFlight = false;
    });
  };
  const onVisible = () => {
    if (document.visibilityState === 'visible') check();
  };
  const timer = window.setInterval(check, intervalMs);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('online', check);
  return () => {
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('online', check);
  };
}
